import { useMemo } from 'react';
import { createStore, useStore } from '../state/createStore';
import { readAppLanguage, writeAppLanguage } from '../state/preferences';
import { RELEASE_DEFAULT_LOCALE, resolvePreference, type AppLanguagePreference, type AppLocale } from './locales';
import { enUS, type MessageKey } from './messages.en-US';
import { zhCN } from './messages.zh-CN';

// The i18n runtime: one locale store, one lookup, one React hook.
//   - App Language is a per-device preference (yanwushi-v2:app-language:v1). Boot resolves
//     persisted → release default (OWNER acceptance zh-CN unless the release configuration
//     flips it to en-US).
//   - Switching is live: every subscriber re-renders; no restart, no remount of the shell.
//   - Report Language is NOT here. It is inspection data (src/domain/inspection.ts) and is never
//     derived from the App Language (OC §9.1, Authority §21.2).
//   - A missing translation is a compile error (messages.zh-CN.ts is Record<MessageKey, string>),
//     so no silent fallback to the other language exists at runtime.

export type { MessageKey } from './messages.en-US';

export const DICTIONARIES: Record<AppLocale, Record<MessageKey, string>> = Object.freeze({
  'en-US': enUS,
  'zh-CN': zhCN
});

export interface LocaleState {
  locale: AppLocale;
  // What the inspector chose in Settings: follow the system, or a pinned locale.
  preference: AppLanguagePreference;
}

export const localeStore = createStore<LocaleState>({ locale: RELEASE_DEFAULT_LOCALE, preference: 'system' });

function deviceLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  const list = Array.isArray(navigator.languages) && navigator.languages.length > 0 ? navigator.languages : [navigator.language];
  return list.filter((l): l is string => typeof l === 'string' && l.length > 0);
}

function applyDocumentLocale(locale: AppLocale): void {
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
}

// Boot: the persisted preference (default: follow the system) resolved against the device
// languages; an unsupported device language falls back to the release default. Returns the
// locale in force.
export function initAppLanguage(): AppLocale {
  const preference = readAppLanguage() ?? 'system';
  const locale = resolvePreference(preference, deviceLanguages());
  localeStore.set(() => ({ locale, preference }));
  applyDocumentLocale(locale);
  return locale;
}

// Settings → App Language: system / 中文 / English. Persisted per device; switching is live.
export function setAppLanguagePreference(preference: AppLanguagePreference): AppLocale {
  writeAppLanguage(preference);
  const locale = resolvePreference(preference, deviceLanguages());
  localeStore.set(() => ({ locale, preference }));
  applyDocumentLocale(locale);
  return locale;
}

// Pins an explicit locale (Settings, tests, development scenarios).
export function setAppLanguage(locale: AppLocale): void {
  setAppLanguagePreference(locale);
}

export function currentLanguagePreference(): AppLanguagePreference {
  return localeStore.get().preference;
}

export function currentLocale(): AppLocale {
  return localeStore.get().locale;
}

export type MessageParams = Record<string, string | number>;

function interpolate(template: string, params?: MessageParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => (name in params ? String(params[name]) : match));
}

export function translate(locale: AppLocale, key: MessageKey, params?: MessageParams): string {
  return interpolate(DICTIONARIES[locale][key], params);
}

export function t(key: MessageKey, params?: MessageParams): string {
  return translate(localeStore.get().locale, key, params);
}

// Plural keys are declared as `<base>.one` / `<base>.other`; the base is typed so a misspelled
// plural base is a compile error too.
export type PluralBase = { [K in MessageKey]: K extends `${infer B}.other` ? B : never }[MessageKey];

function pluralCategory(locale: AppLocale, n: number): 'one' | 'other' {
  try {
    return new Intl.PluralRules(locale).select(n) === 'one' ? 'one' : 'other';
  } catch {
    return n === 1 ? 'one' : 'other';
  }
}

export function translatePlural(locale: AppLocale, base: PluralBase, n: number, params?: MessageParams): string {
  const key = `${base}.${pluralCategory(locale, n)}` as MessageKey;
  return translate(locale, key, { n, ...params });
}

export function tp(base: PluralBase, n: number, params?: MessageParams): string {
  return translatePlural(localeStore.get().locale, base, n, params);
}

export interface Translator {
  locale: AppLocale;
  t: (key: MessageKey, params?: MessageParams) => string;
  tp: (base: PluralBase, n: number, params?: MessageParams) => string;
}

export function useLocale(): AppLocale {
  return useStore(localeStore, (s) => s.locale);
}

export function useT(): Translator {
  const locale = useLocale();
  return useMemo<Translator>(
    () => ({
      locale,
      t: (key, params) => translate(locale, key, params),
      tp: (base, n, params) => translatePlural(locale, base, n, params)
    }),
    [locale]
  );
}

// The App Language switcher options, in the order the owner listed them (中文 · English).
export const APP_LANGUAGE_OPTIONS: ReadonlyArray<{ locale: AppLocale; labelKey: MessageKey }> = Object.freeze([
  { locale: 'zh-CN', labelKey: 'language.chinese' },
  { locale: 'en-US', labelKey: 'language.english' }
]);
